import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, LogOut, Search, ExternalLink, AlertTriangle } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { fetchSalesPipeline } from "@/lib/api";
import AdminNavTabs from "@/components/AdminNavTabs";

const STAGE_LABEL = {
  not_contacted: "Not Contacted",
  contacted: "Contacted",
  delivered_or_seen: "Delivered / Seen",
  concept_viewed: "Concept Viewed",
  replied: "Replied",
  conversation: "Conversation",
  proposal_sent: "Proposal Sent",
  won: "Won",
  lost: "Lost",
};

const STAGE_BADGE_CLASS = {
  not_contacted: "bg-slate-100 text-slate-600",
  contacted: "bg-sky-100 text-sky-700",
  delivered_or_seen: "bg-sky-100 text-sky-700",
  concept_viewed: "bg-indigo-100 text-indigo-700",
  replied: "bg-amber-100 text-amber-800",
  conversation: "bg-amber-100 text-amber-800",
  proposal_sent: "bg-violet-100 text-violet-700",
  won: "bg-emerald-100 text-emerald-800",
  lost: "bg-rose-100 text-rose-700",
};

const WEBSITE_STATUS_LABEL = {
  offline_concept: "Offline Concept",
  proposal_accepted: "Proposal Accepted",
  online_client: "Online Client",
  rejected: "Rejected",
};

const CATEGORY_LABEL = {
  jewelry: "Jewelry",
  dental: "Dental",
  construction: "Construction",
  "real-estate": "Real Estate",
  "wedding-events": "Wedding & Events",
  "law-accounting": "Law & Accounting",
};

const CHANNEL_LABEL = {
  messenger: "Messenger", facebook: "Facebook", instagram: "Instagram", email: "Email",
  whatsapp: "WhatsApp", phone: "Phone", sms: "SMS", linkedin: "LinkedIn", in_person: "In Person", other: "Other",
};

const PLAN_LABEL = { ownership: "Ownership", maintenance: "Maintenance", responsibility: "Responsibility", priority: "Priority & Reputation" };

function StageBadge({ stage }) {
  const s = stage || "not_contacted";
  return <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap ${STAGE_BADGE_CLASS[s] || "bg-slate-100 text-slate-600"}`}>{STAGE_LABEL[s] || s}</span>;
}

function FollowUpBadge({ date }) {
  if (!date) return <span className="text-slate-400 text-xs">—</span>;
  const today = new Date().toISOString().slice(0, 10);
  const isOverdue = date < today;
  const isToday = date === today;
  const cls = isOverdue ? "text-rose-600 font-semibold" : isToday ? "text-amber-600 font-semibold" : "text-slate-600";
  return <span className={`text-xs ${cls}`}>{date}{isOverdue && " (overdue)"}{isToday && " (today)"}</span>;
}

export default function AdminSales() {
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [plan, setPlan] = useState("");
  const [channel, setChannel] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [sort, setSort] = useState("business_name");

  const load = () => {
    if (!token) return;
    setLoading(true);
    fetchSalesPipeline(token, {
      search: search || undefined, stage: stage || undefined, category: category || undefined,
      priority: priority || undefined, plan: plan || undefined, channel: channel || undefined,
      followUp: followUp || undefined, sort,
    })
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || "Could not load the sales pipeline."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, search, stage, category, priority, plan, channel, followUp, sort]);

  const categories = useMemo(() => {
    const set = new Set((data?.items || []).map((s) => s.category));
    return [...set];
  }, [data]);

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">Logged in as {email}</span></div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold">Sales Pipeline</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">Manual outreach tracking for the 69 current prospects. Nothing here contacts anyone automatically.</p>
          </div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"><LogOut size={15} /> Log out</button>
        </div>
        <AdminNavTabs />

        {data && (
          <>
            <div className="mt-8 grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
              {Object.keys(STAGE_LABEL).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStage(stage === s ? "" : s)}
                  className={`rounded-xl border p-3 text-left transition-colors ${stage === s ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <p className="text-xl font-bold">{data.stage_counts[s] ?? 0}</p>
                  <p className="text-[11px] text-slate-500 leading-tight">{STAGE_LABEL[s]}</p>
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-3">
              <button type="button" onClick={() => setFollowUp(followUp === "overdue" ? "" : "overdue")} className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold ${followUp === "overdue" ? "border-rose-400 bg-rose-50 text-rose-700" : "border-slate-200 bg-white text-slate-700"}`}>
                <AlertTriangle size={14} /> {data.follow_ups_overdue} Overdue Follow-up{data.follow_ups_overdue === 1 ? "" : "s"}
              </button>
              <button type="button" onClick={() => setFollowUp(followUp === "due_today" ? "" : "due_today")} className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold ${followUp === "due_today" ? "border-amber-400 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-700"}`}>
                {data.follow_ups_due} Due Today
              </button>
              <button type="button" onClick={() => setFollowUp(followUp === "next_7_days" ? "" : "next_7_days")} className={`rounded-lg border px-4 py-2 text-sm font-semibold ${followUp === "next_7_days" ? "border-slate-400 bg-slate-100" : "border-slate-200 bg-white text-slate-700"}`}>
                Next 7 Days
              </button>
            </div>

            <details className="mt-6 rounded-xl border border-slate-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-700">Vertical performance ({Object.keys(data.vertical_breakdown).length} categories)</summary>
              <div className="overflow-x-auto border-t border-slate-100">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500"><th className="px-4 py-2">Vertical</th><th className="px-4 py-2">Contacted</th><th className="px-4 py-2">Replied</th><th className="px-4 py-2">Proposal</th><th className="px-4 py-2">Won</th></tr></thead>
                  <tbody>
                    {Object.entries(data.vertical_breakdown).map(([cat, v]) => (
                      <tr key={cat} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-medium">{CATEGORY_LABEL[cat] || cat}</td>
                        <td className="px-4 py-2">{v.contacted}</td>
                        <td className="px-4 py-2">{v.replied}</td>
                        <td className="px-4 py-2">{v.proposal_sent}</td>
                        <td className="px-4 py-2">{v.won}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search business, city, decision-maker…" className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2.5 text-sm" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All verticals</option>
            {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>)}
          </select>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All priorities</option>
            <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
          </select>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All recommended plans</option>
            {Object.entries(PLAN_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="">All channels</option>
            {Object.entries(CHANNEL_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm">
            <option value="business_name">Sort: Business Name</option>
            <option value="priority">Sort: Priority</option>
            <option value="follow_up_date">Sort: Follow-up Date</option>
            <option value="last_contact">Sort: Last Contact</option>
            <option value="sales_stage">Sort: Sales Stage</option>
          </select>
        </div>

        {error && <p className="mt-4 text-sm text-rose-600" role="status">{error}</p>}
        {loading && <p className="mt-6 text-sm text-slate-500">Loading…</p>}

        {!loading && data && (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-3">Business</th>
                    <th className="px-4 py-3">Vertical</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Sales Stage</th>
                    <th className="px-4 py-3">Website Status</th>
                    <th className="px-4 py-3">Channel</th>
                    <th className="px-4 py-3">Last Contact</th>
                    <th className="px-4 py-3">Follow-up</th>
                    <th className="px-4 py-3">Rec. Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((site) => (
                    <tr key={site.slug} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold"><Link to={`/admin/sales/${site.slug}`} className="hover:text-emerald-700">{site.business_name}</Link></td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{CATEGORY_LABEL[site.category] || site.category}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs capitalize">{site.sales_priority || "—"}</td>
                      <td className="px-4 py-3"><StageBadge stage={site.sales_stage} /></td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{WEBSITE_STATUS_LABEL[site.status] || site.status}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{CHANNEL_LABEL[site.primary_contact_channel] || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{site.last_contacted_at ? new Date(site.last_contacted_at).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-3"><FollowUpBadge date={site.next_follow_up_at} /></td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{PLAN_LABEL[site.recommended_plan] || "—"}</td>
                    </tr>
                  ))}
                  {data.items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No prospects match these filters.</td></tr>}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 md:hidden space-y-3">
              {data.items.map((site) => (
                <Link key={site.slug} to={`/admin/sales/${site.slug}`} className="block rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold">{site.business_name}</p>
                      <p className="text-xs text-slate-500">{CATEGORY_LABEL[site.category] || site.category} · {site.sales_priority ? `${site.sales_priority} priority` : "priority unset"}</p>
                    </div>
                    <StageBadge stage={site.sales_stage} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{WEBSITE_STATUS_LABEL[site.status] || site.status}</span>
                    <span>{CHANNEL_LABEL[site.primary_contact_channel] || "no channel set"}</span>
                    <FollowUpBadge date={site.next_follow_up_at} />
                  </div>
                </Link>
              ))}
              {data.items.length === 0 && <p className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-slate-500 text-sm">No prospects match these filters.</p>}
            </div>
          </>
        )}

        <p className="mt-8 text-xs text-slate-400">
          <ExternalLink size={11} className="inline -mt-0.5 mr-1" />
          Sales stage tracks outreach progress only — it never changes a site's public website status automatically.
        </p>
      </div>
    </div>
  );
}
